/**
 * Lab Product Card Video Hover
 *
 * Handles video autoplay on hover for product cards.
 * Videos are lazy loaded and only play when the user hovers over the card.
 *
 * Ecom Project (17-oct-2026):
 * - The <script> tag is rendered once per card with video, so this file can run
 *   many times per page. Only the first run does anything.
 * - Each card is set up once (data-video-hover) instead of adding listeners again
 *   on every DOM change.
 * - Touch screens are skipped: the video only shows on hover (see the CSS in
 *   lab-product-card.liquid), so loading it on touch played it hidden.
 */

(function() {
  'use strict';

  if (window.labProductCardVideoReady) return;
  window.labProductCardVideoReady = true;

  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  function setupCard(card) {
    card.setAttribute('data-video-hover', '');

    const videoContainer = card.querySelector('.lab-product-card-video');
    const deferredMedia = videoContainer && videoContainer.querySelector('deferred-media');
    if (!deferredMedia) return;

    const imageContainer = card.querySelector('.lab-product-card-image');
    let videoElement = null;

    function playVideo() {
      if (videoElement && videoElement.tagName === 'VIDEO') {
        videoElement.muted = true;
        videoElement.play().catch(() => {});
      } else if (videoElement && videoElement.tagName === 'IFRAME' && videoElement.contentWindow) {
        if (videoElement.src.includes('youtube.com')) {
          videoElement.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        } else if (videoElement.src.includes('vimeo.com')) {
          videoElement.contentWindow.postMessage('{"method":"play"}', '*');
        }
      }
    }

    function loadVideo() {
      if (videoElement) return;

      // Insert the <template> content (theme's deferred-media), without moving focus
      if (typeof deferredMedia.loadContent === 'function') {
        deferredMedia.loadContent(false);
      } else if (!deferredMedia.querySelector('video, iframe')) {
        const template = deferredMedia.querySelector('template');
        if (template) deferredMedia.appendChild(template.content.cloneNode(true));
      }

      videoElement = deferredMedia.querySelector('video, iframe');
      if (videoElement && videoElement.tagName === 'VIDEO') {
        videoElement.loop = true;
        videoElement.muted = true;
        videoElement.playsInline = true;
        videoElement.controls = false;
      }
    }

    function pauseVideo() {
      if (videoElement && videoElement.tagName === 'VIDEO') {
        videoElement.pause();
        videoElement.currentTime = 0;
      } else if (videoElement && videoElement.tagName === 'IFRAME' && videoElement.contentWindow) {
        if (videoElement.src.includes('youtube.com')) {
          videoElement.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        } else if (videoElement.src.includes('vimeo.com')) {
          videoElement.contentWindow.postMessage('{"method":"pause"}', '*');
        }
      }
      videoContainer.style.opacity = '0';
      videoContainer.style.visibility = 'hidden';
      if (imageContainer) imageContainer.style.opacity = '1';
    }

    card.addEventListener('mouseenter', () => {
      loadVideo();
      playVideo();
      videoContainer.style.opacity = '1';
      videoContainer.style.visibility = 'visible';
      if (imageContainer) imageContainer.style.opacity = '0';
    });

    card.addEventListener('mouseleave', pauseVideo);
  }

  function initVideoHover() {
    if (!canHover.matches) return;
    document
      .querySelectorAll('.lab-product-card-gallery--has-video:not([data-video-hover])')
      .forEach(setupCard);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVideoHover);
  } else {
    initVideoHover();
  }

  // Re-initialize on section re-render (for Shopify theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', initVideoHover);
  }

  // New cards (filters, pagination): one observer, at most one pass per frame
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      initVideoHover();
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();
